/*!
 * Oneskin Product Recommendations — vanilla JS controller for the
 * "Recommended products" (Shopify product recommendations API) and
 * "Recently viewed products" (localStorage) sections.
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'product-recommendations.js' | asset_url }}" defer="defer"></script>
 *
 * Replaces:
 *   frontend/scripts/sections/recommended-products.js
 *   frontend/scripts/sections/recently-viewed-products.js
 *   frontend/scripts/lib/recently-viewed-products.js (getRecentProducts reader —
 *     the writer, updateRecentProducts, now lives in assets/product.js)
 *   frontend/scripts/lib/carousel.js (Swiper wrapper -> native scroll-snap)
 *   frontend/scripts/lib/animation/list-slider.js
 *
 * Preserves all existing data-attribute/class hooks (`[data-section-type="recommended-products"]`,
 * `[data-section-type="recently-viewed-products"]`, `.carousel`/`.swiper`/`.swiper-wrapper`/
 * `.swiper-slide`, `[data-next]`/`[data-prev]`) so no Liquid markup changes were required
 * for this pass.
 */
;(() => {
  'use strict'

  const STORAGE_KEY = 'fluco_sto_recentlyViewed'

  /* -------------------------------------------------------------------------
   * Shared helpers
   * ---------------------------------------------------------------------- */

  function getMediaQuery(size) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(`--media-${size}`)
    return value || false
  }

  function shouldAnimate(node) {
    return node.classList.contains('animation') && !document.documentElement.classList.contains('prefers-reduced-motion')
  }

  function delayOffset(node, selectors, offsetStart = 0) {
    let offset = offsetStart
    selectors.forEach((selector) => {
      node.querySelectorAll(selector).forEach((item) => {
        item.style.setProperty('--delay-offset-multiplier', offset)
        offset++
      })
    })
  }

  function initListSliderAnimation(node) {
    delayOffset(node, ['.animation--section-introduction > *', '.animation--controls', '.animation--item'])

    const margin = window.matchMedia(getMediaQuery('above-720')).matches ? 200 : 100
    const threshold = Math.min(margin / (node.offsetHeight || 1), 0.5)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add('is-visible')
          observer.disconnect()
        }
      },
      { threshold }
    )
    observer.observe(node)

    return {
      destroy() {
        observer.disconnect()
      },
    }
  }

  function getRecentProducts() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY))
    } catch (e) {
      return null
    }
  }

  /* -------------------------------------------------------------------------
   * Native responsive carousel (replaces the Swiper wrapper in lib/carousel.js)
   * Supports a mobile-first config plus numeric-keyed breakpoints, mirroring
   * the Swiper `breakpoints` option shape used by the original sections.
   * ---------------------------------------------------------------------- */

  function initResponsiveCarousel(node, options = {}) {
    const viewport = node.classList.contains('swiper') ? node : node.querySelector('.swiper')
    if (!viewport) return null

    const track = viewport.querySelector('.swiper-wrapper') || viewport
    track.classList.add('flex', 'snap-x', 'snap-mandatory', 'overflow-x-auto', 'scroll-smooth')
    viewport.classList.add('overflow-hidden')

    const slides = Array.from(track.children)
    slides.forEach((slide) => slide.classList.add('snap-start', 'shrink-0'))

    const breakpointEntries = Object.keys(options.breakpoints || {})
      .map(Number)
      .sort((a, b) => a - b)

    function activeConfig() {
      const width = window.innerWidth
      let config = { slidesPerView: options.slidesPerView, spaceBetween: options.spaceBetween }
      breakpointEntries.forEach((bp) => {
        if (width >= bp) config = Object.assign({}, config, options.breakpoints[bp])
      })
      return config
    }

    function applyConfig() {
      const { slidesPerView, spaceBetween } = activeConfig()
      if (slidesPerView) {
        const percent = 100 / slidesPerView
        slides.forEach((slide) => {
          slide.style.width = `${percent}%`
        })
      }
      if (spaceBetween != null) track.style.gap = `${spaceBetween}px`
      updateOverflow()
    }

    function updateOverflow() {
      const hasOverflow = track.scrollWidth > track.clientWidth + 1
      viewport.classList.toggle('has-overflow', hasOverflow)
    }

    const nextButton = node.querySelector('[data-next]')
    const prevButton = node.querySelector('[data-prev]')

    function scrollByAmount(direction) {
      const amount = viewport.clientWidth * 0.8 * direction
      track.scrollBy({ left: amount, behavior: 'smooth' })
    }

    function updateNavButtons() {
      const atStart = track.scrollLeft <= 1
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1
      prevButton?.classList.toggle('swiper-button-disabled', atStart)
      nextButton?.classList.toggle('swiper-button-disabled', atEnd)
      if (prevButton) prevButton.classList.toggle('swiper-button-lock', !viewport.classList.contains('has-overflow'))
      if (nextButton) nextButton.classList.toggle('swiper-button-lock', !viewport.classList.contains('has-overflow'))
    }

    const nextHandler = () => scrollByAmount(1)
    const prevHandler = () => scrollByAmount(-1)
    nextButton?.addEventListener('click', nextHandler)
    prevButton?.addEventListener('click', prevHandler)

    const scrollHandler = () => updateNavButtons()
    track.addEventListener('scroll', scrollHandler, { passive: true })

    function handleFocus(event) {
      if (!document.body.classList.contains('user-is-tabbing')) return
      const slide = event.target.closest('.swiper-slide')
      if (!slide) return
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
    track.addEventListener('focusin', handleFocus)

    const resizeHandler = () => applyConfig()
    window.addEventListener('resize', resizeHandler)

    applyConfig()
    updateNavButtons()

    return {
      destroy() {
        nextButton?.removeEventListener('click', nextHandler)
        prevButton?.removeEventListener('click', prevHandler)
        track.removeEventListener('scroll', scrollHandler)
        track.removeEventListener('focusin', handleFocus)
        window.removeEventListener('resize', resizeHandler)
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Recommended products (Shopify product recommendations API)
   * ---------------------------------------------------------------------- */

  function initRecommendedProducts(container) {
    const content = container.querySelector('[data-recommendations]')
    if (!content) return { unload() {} }

    const { limit, productId: id, sectionId, productsPerView, mobileProductsPerView } = container.dataset
    const perView = parseInt(productsPerView, 10)
    const mobilePerView = parseInt(mobileProductsPerView, 10) * 1.07

    let productItem = null
    let listAnimation = null
    let carousel = null

    function removeSection() {
      container.parentNode?.removeChild(container)
    }

    const routes = (window.theme && window.theme.routes) || {}
    const requestUrl = `${routes.productRecommendations || '/recommendations/products'}?section_id=${sectionId}&limit=${limit}&product_id=${id}`

    fetch(requestUrl)
      .then((response) => response.text())
      .then((html) => {
        const wrapper = document.createElement('div')
        wrapper.innerHTML = html
        const recommendations = wrapper.querySelector('[data-recommendations]')
        if (!recommendations) {
          removeSection()
          return
        }
        content.innerHTML = recommendations.innerHTML

        const carouselSlide = content.querySelector('.carousel__slide')
        productItem = window.OneskinProductItem ? window.OneskinProductItem(container) : null

        if (shouldAnimate(container)) listAnimation = initListSliderAnimation(container)

        if (carouselSlide) {
          carousel = initResponsiveCarousel(content, {
            slidesPerView: mobilePerView,
            spaceBetween: 6,
            breakpoints: {
              720: { slidesPerView: perView === 5 ? perView - 1 : perView },
              1200: { slidesPerView: perView },
            },
          })
        } else {
          removeSection()
        }
      })
      .catch(() => removeSection())

    return {
      unload() {
        productItem?.unload?.()
        listAnimation?.destroy()
        carousel?.destroy()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Recently viewed products (localStorage)
   * ---------------------------------------------------------------------- */

  function initRecentlyViewedProducts(container) {
    const { limit, productsPerView, mobileProductsPerView, productHandle } = container.dataset
    const parsedLimit = parseInt(limit, 10)
    const perView = parseInt(productsPerView, 10)
    const mobilePerView = parseInt(mobileProductsPerView, 10) * 1.05

    const recentProductsContainer = container.querySelector('.recently-viewed-products__slider-wrapper')

    let productItem = null
    let listAnimation = null
    let carousel = null

    function removeSection() {
      container.parentNode?.removeChild(container)
    }

    const recentProducts = getRecentProducts()
    if (!(recentProducts || []).length) {
      removeSection()
      return { unload() {} }
    }

    const recentProductsTrimmed = recentProducts.filter((handle) => handle !== productHandle).slice(0, parsedLimit)

    if (!recentProductsTrimmed.length) {
      removeSection()
      return { unload() {} }
    }

    function renderProductItem(productHandle) {
      const requestUrl = `${(window.theme && window.theme.routes && window.theme.routes.products) || '/products'}/${productHandle}?section_id=recently-viewed-product-item`

      return fetch(requestUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`Recent product "${productHandle}" not found`)
          return response.text()
        })
        .then((html) => {
          const wrapper = document.createElement('div')
          wrapper.innerHTML = html
          const productItemEl = wrapper.querySelector('[data-recently-viewed-item]')
          if (productItemEl && recentProductsContainer) {
            recentProductsContainer.insertAdjacentHTML('beforeend', productItemEl.innerHTML)
          }
        })
        .catch((error) => console.error('[product-recommendations]', error.message))
    }

    Promise.all(recentProductsTrimmed.map(renderProductItem)).then(() => {
      const carouselSlide = container.querySelector('.carousel__slide')
      productItem = window.OneskinProductItem ? window.OneskinProductItem(container) : null

      if (shouldAnimate(container)) listAnimation = initListSliderAnimation(container)

      if (carouselSlide) {
        carousel = initResponsiveCarousel(container, {
          slidesPerView: mobilePerView,
          spaceBetween: 12,
          breakpoints: {
            720: { spaceBetween: 16, slidesPerView: perView === 5 ? perView - 1 : perView },
            1200: { spaceBetween: 24, slidesPerView: perView },
          },
        })
      } else {
        removeSection()
      }
    })

    return {
      unload() {
        productItem?.unload?.()
        listAnimation?.destroy()
        carousel?.destroy()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Auto init / theme editor section lifecycle
   * ---------------------------------------------------------------------- */

  const instances = new WeakMap()

  function initContainer(container) {
    if (instances.has(container)) return
    const { sectionType } = container.dataset
    let instance = null
    if (sectionType === 'recommended-products') instance = initRecommendedProducts(container)
    else if (sectionType === 'recently-viewed-products') instance = initRecentlyViewedProducts(container)
    if (instance) instances.set(container, instance)
  }

  function unloadContainer(container) {
    instances.get(container)?.unload?.()
    instances.delete(container)
  }

  function autoInit(root = document) {
    root
      .querySelectorAll('[data-section-type="recommended-products"], [data-section-type="recently-viewed-products"]')
      .forEach(initContainer)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit())
  } else {
    autoInit()
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionId = event.detail?.sectionId
    const container = sectionId
      ? document.querySelector(`[data-section-id="${sectionId}"]`)
      : null
    if (container) initContainer(container)
    else autoInit(event.target)
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId
    const container = sectionId ? document.querySelector(`[data-section-id="${sectionId}"]`) : null
    if (container) unloadContainer(container)
  })
})()
